import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';
import i18n from '@/i18n';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: React.ComponentType<{ error: Error; resetError: () => void }>;
  resetKeys?: unknown[];
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * ErrorBoundary component to catch React rendering errors
 *
 * Provides fallback UI with retry functionality and automatic reset
 * when resetKeys change.
 *
 * @example
 * ```tsx
 * <ErrorBoundary resetKeys={[userId]}>
 *   <UserProfile userId={userId} />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error to console in development
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);

    // Call optional error handler (could send to Sentry, etc.)
    this.props.onError?.(error, errorInfo);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    const { resetKeys } = this.props;
    const { hasError } = this.state;

    // Auto-reset when resetKeys change
    if (hasError && resetKeys && prevProps.resetKeys) {
      const hasKeysChanged = resetKeys.some((key, index) => key !== prevProps.resetKeys?.[index]);
      if (hasKeysChanged) {
        this.resetError();
      }
    }
  }

  resetError = (): void => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  render(): ReactNode {
    const { hasError, error } = this.state;
    const { children, fallback: FallbackComponent } = this.props;

    if (hasError && error) {
      // Use custom fallback if provided
      if (FallbackComponent) {
        return <FallbackComponent error={error} resetError={this.resetError} />;
      }

      // Default fallback UI
      return (
        <div className="flex min-h-[400px] w-full items-center justify-center p-8">
          <div className="max-w-md space-y-4 text-center">
            <div className="flex justify-center">
              <AlertTriangle className="h-16 w-16 text-destructive" />
            </div>
            <div className="space-y-2">
              <h2 className="text-2xl font-semibold tracking-tight">
                {i18n.t('errorBoundary.title')}
              </h2>
              <p className="text-sm text-muted-foreground">{i18n.t('errorBoundary.description')}</p>
              {import.meta.env.DEV && error && (
                <details className="mt-4 rounded-lg bg-muted p-4 text-left">
                  <summary className="cursor-pointer text-sm font-medium">
                    Error details (dev only)
                  </summary>
                  <pre className="mt-2 overflow-auto text-xs">
                    {error.message}
                    {'\n\n'}
                    {error.stack}
                  </pre>
                </details>
              )}
            </div>
            <div className="flex justify-center gap-2">
              <Button onClick={this.resetError} variant="default">
                {i18n.t('errorBoundary.tryAgain')}
              </Button>
              <Button onClick={() => window.location.reload()} variant="outline">
                {i18n.t('errorBoundary.reloadPage')}
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return children;
  }
}
